import * as dayjs from "dayjs";
import { graphql, navigate } from 'gatsby';
import { StaticImage } from "gatsby-plugin-image";
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';
import { Helmet } from "react-helmet";
import styled from "styled-components";
import BlogPostCard from '../components/blog-post-card';
import DigitalBackdrop from '../components/digital-backdrop';
import Footer from "../components/footer";
import Header from "../components/header";
import favicon from '../images/favicon.png';


const TopRightTree = styled.div`
   position: absolute;
   top: -19px;
   right: -250px;
   width: 850px;
   z-index: 1;

   @media (max-width: 768px) {
    right: -265px;
    width: 450px !important;
  }

  @media (max-width:1180px ) {
    width: 760px;
  }

  @media (max-width: 900px) {
    width: 600px;
    right: -245px;
  }
`;


// Components & Sections
const BlogPageWrapper = styled.div`
   background-color: white;
   margin: 0;
   overflow: hidden;
   position: relative;
   min-height: 100vh;
 `;

const BlogPageInner = styled.div`
    max-width: 1060px; 
    margin-left: auto;
    margin-right: auto;
    padding: 180px 80px 180px 80px;
    height: 100%;
    z-index: 10;
    position: relative;
    margin-bottom: 160px;
   @media (max-width: 900px) {
     max-width: 100%;
     padding: 0 80px 0px 80px;
     margin-top: 86px;
  }

  @media (max-width: 768px) {
     margin-top: 86px;
     padding: 0 26px 0 26px;
     z-index: 36;
  }
 `

const BlogHeader = styled.div`
    margin-top: 24px;
    margin-bottom: 16px;
`

const BlogHeading = styled.h1`
    font-size: 48px;
    line-height: 56px;
    margin: 0 0 8px 0;

    @media (max-width: 768px) {
        font-size: 36px;
        line-height: 42px;
    }
`

const BlogIntro = styled.p`
    color: rgba(117, 117, 117, 1);
    font-size: 16px;
    line-height: 24px;
    margin: 0;
    max-width: 560px;
`

const BlogHeadingPost = styled.div`
    display: flex;
    width: 100%;
    height: 340px;
    cursor: pointer;
    margin-top: 40px;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 32px rgba(0, 0, 0, 0.1);
    }

    @media (max-width: 768px) {
        flex-direction: column;
        height: 100%;
    }
 `

const BlogHeadingImgDiv = styled.div`
    width: 55%;
    height: 340px;
    min-width: 300px;

    @media (max-width: 768px) {
      width: 100%;
      height: 220px;
    }
 `

const BlogHeadingImg = styled.img`
    height: 100%;
    width: 100%;
    object-fit: cover;
    display: block;
 `

const BlogHeadingText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px;
    flex: 1;

    @media (max-width: 768px) {
        padding: 24px;
    }
 `

const Subtitle = styled.p`
    font-size: 12px;
    color: #4fbc61;
    font-weight: 500;
    margin: 0 0 12px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
 `

const Title = styled.h2`
    color: black;
    margin: 0 0 12px 0;
    font-size: 32px;
    line-height: 38px;

    @media (max-width: 768px) {
        font-size: 24px;
        line-height: 30px;
    }
 `

const Text = styled.p`
    color: rgba(79, 79, 79, 1);
    font-size: 15px;
    line-height: 24px;
    margin: 0;
 `

const CardsGrid = styled.div`
    display: grid;
    grid-template: auto / repeat(3, 1fr);
    margin-top: 80px;
    gap: 40px;
    z-index: 89;
    position: relative;
    justify-items: stretch;

    @media (max-width: 1100px) {
        grid-template: auto / repeat(2, 1fr);
    }

    @media (max-width: 768px) {
     grid-template: auto / 1fr;
     grid-row-gap: 56px;
     margin-top: 64px;
    }

    @media (max-width: 500px) {
      grid-row-gap: 26px;
    }
`

const BlogHeadingFooter = styled.div`
  color: rgba(117, 117, 117, 1);
  font-size: 12px;
  margin-top: 20px;
  display: flex;
  align-items: center;
`

const Tag = styled.span`
  background-color: rgba(79, 188, 97, 0.12);
  border-radius: 18px;
  padding: 4px 10px;
  margin-right: 12px;
  color: #4fbc61;
  font-weight: 500;
`
const BlogPage = ({ data }) => {
	const posts = data.gcms.posts;
	const { t } = useTranslation();
	const navigateToPost = (slug) => {
		navigate(`/posts/${slug}`);
	}

	return (
		<BlogPageWrapper>
			<Header />
			<Helmet>
				<meta charSet="utf-8" />
				<title>{t("blog.page.title")}</title>
				<link rel="canonical" href="https://springtree.eu" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<link rel="icon" type="image/png" href={favicon} sizes="16x16" />
				<meta name="twitter:card" content="summary"></meta>
			</Helmet>
			<DigitalBackdrop />
			<TopRightTree>
				<StaticImage loading="eager" placeholder="none" src='../images/top-right-tree.png' alt="tree leaves" />
			</TopRightTree>

			<BlogPageInner>
				<BlogHeader>
					<BlogHeading>{t("blog.heading")}</BlogHeading>
					<BlogIntro>{t("blog.intro")}</BlogIntro>
				</BlogHeader>
				{posts ? (
					<BlogHeadingPost onClick={() => navigateToPost(posts[0].slug)}>
						<BlogHeadingImgDiv >
							<BlogHeadingImg src={posts[0]?.coverImage?.url} alt={t("blog.cover.alt")} />
						</BlogHeadingImgDiv>
						<BlogHeadingText>

							<Subtitle>{dayjs(posts[0]?.date).format("MMMM DD, YYYY")}{posts[0]?.author?.name ? ` — ${posts[0].author.name}` : ''}</Subtitle>
							<Title>{posts[0]?.title}</Title>
							<Text>{posts[0]?.excerpt}</Text>

							<BlogHeadingFooter>
								{posts[0]?.tags?.[0] && <Tag>{posts[0].tags[0]}</Tag>}
								{posts[0]?.timeToRead} {t("blog.min.read")}
							</BlogHeadingFooter>
						</BlogHeadingText>
					</BlogHeadingPost>
				) : <>{t("blog.loading")}</>}

				<CardsGrid>
					{posts ? posts.slice(1).map((post) => (
						<div key={post.id}>
							<BlogPostCard post={post} />
						</div>
					)) : <></>}
				</CardsGrid>

			</BlogPageInner>
			<Footer />
		</BlogPageWrapper>
	)
}


export const postsQuery = graphql`
 query ($language: String!) {
	locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  gcms {
    posts(orderBy: createdAt_DESC) {
      id
      slug
      tags
      timeToRead
      title
      publishedAt
      excerpt
      date
      createdAt
      coverImage {
        url
      }
      author {
        name
        title
        picture {
          url
        }
        title
      }
    }
  }

}`

export default BlogPage;
